import { z } from 'zod';

// Players in the admin dashboard (players.read to look; players.support to help; players.ban to suspend,
// ban or delete). Every action is audited with a reason; dangerous ones need a fresh two-step code.

export const PLAYER_STATUSES = {
  active: 'Active',
  incomplete: 'Profile not finished',
  age_restricted: 'Blocked: under 18',
  suspended: 'Suspended',
  banned: 'Banned',
  deleted: 'Deleted',
} as const;
export type PlayerStatus = keyof typeof PLAYER_STATUSES;

export const PlayerSummary = z.object({
  id: z.string().uuid(),
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  status: z.enum(Object.keys(PLAYER_STATUSES) as [PlayerStatus, ...PlayerStatus[]]),
  suspendedUntil: z.string().nullable(),
  moderationReason: z.string().nullable(),
  isTest: z.boolean(),
  country: z.string().nullable(),
  joinedAt: z.string(),
  lastSeenAt: z.string().nullable(),
  // the active life, if any
  life: z
    .object({ id: z.string().uuid(), firstName: z.string(), walletPence: z.number().int(), startedAt: z.string() })
    .nullable(),
});
export type PlayerSummary = z.infer<typeof PlayerSummary>;

export const PlayerListQuery = z.object({
  q: z.string().trim().max(80).optional(),
  status: z.enum(['all', ...(Object.keys(PLAYER_STATUSES) as PlayerStatus[])] as [string, ...string[]]).default('all'),
  page: z.coerce.number().int().min(1).default(1),
});
export type PlayerListQuery = z.infer<typeof PlayerListQuery>;
export type PlayerFilterKey = 'all' | PlayerStatus;
export const PLAYERS_PAGE_SIZE = 25;
export const PlayerListReply = z.object({
  players: z.array(PlayerSummary),
  total: z.number().int(),
  page: z.number().int(),
});
export type PlayerListReply = z.infer<typeof PlayerListReply>;

export const PlayerNote = z.object({
  id: z.string().uuid(),
  kind: z.enum(['note', 'warning']),
  text: z.string(),
  by: z.string().nullable(),
  createdAt: z.string(),
  seenAt: z.string().nullable(),
});
export type PlayerNote = z.infer<typeof PlayerNote>;
export const PlayerAuditEntry = z.object({
  id: z.string(),
  action: z.string(),
  by: z.string().nullable(),
  createdAt: z.string(),
  details: z.unknown().nullable(),
});
export const PlayerDetailReply = z.object({
  player: PlayerSummary,
  notes: z.array(PlayerNote),
  // what has happened to this account, newest first
  audit: z.array(PlayerAuditEntry),
  // the character's recent actions, newest first
  recentActions: z.array(
    z.object({ kind: z.string(), target: z.string(), amountPence: z.number().int(), at: z.string() }),
  ),
  openSessions: z.number().int(),
});
export type PlayerDetailReply = z.infer<typeof PlayerDetailReply>;

const reason = z.string().trim().min(5, 'Give a reason of at least 5 characters').max(500);
export const PlayerActionReply = z.object({ player: PlayerSummary, message: z.string() });
export type PlayerActionReply = z.infer<typeof PlayerActionReply>;
export const PLAYER_ACTIONS = {
  // players.support
  note: z.object({ action: z.literal('note'), text: z.string().trim().min(2).max(1000) }),
  warn: z.object({ action: z.literal('warn'), text: z.string().trim().min(5).max(500) }),
  force_rename: z.object({ action: z.literal('force_rename'), reason }),
  sign_out_everywhere: z.object({ action: z.literal('sign_out_everywhere'), reason }),
  verify_email: z.object({ action: z.literal('verify_email'), reason }),
  mark_test: z.object({ action: z.literal('mark_test'), isTest: z.boolean(), reason }),
  // players.ban (a fresh two-step code)
  suspend: z.object({ action: z.literal('suspend'), days: z.number().int().min(1).max(365), reason }),
  unsuspend: z.object({ action: z.literal('unsuspend'), reason }),
  ban: z.object({ action: z.literal('ban'), reason }),
  unban: z.object({ action: z.literal('unban'), reason }),
  delete: z.object({ action: z.literal('delete'), reason }),
} as const;
export const PlayerAction = z.discriminatedUnion('action', [
  PLAYER_ACTIONS.note,
  PLAYER_ACTIONS.warn,
  PLAYER_ACTIONS.force_rename,
  PLAYER_ACTIONS.sign_out_everywhere,
  PLAYER_ACTIONS.verify_email,
  PLAYER_ACTIONS.mark_test,
  PLAYER_ACTIONS.suspend,
  PLAYER_ACTIONS.unsuspend,
  PLAYER_ACTIONS.ban,
  PLAYER_ACTIONS.unban,
  PLAYER_ACTIONS.delete,
]);
export type PlayerAction = z.infer<typeof PlayerAction>;
export const SUPPORT_ACTIONS: PlayerAction['action'][] = [
  'note',
  'warn',
  'force_rename',
  'sign_out_everywhere',
  'verify_email',
  'mark_test',
];
export const BAN_ACTIONS: PlayerAction['action'][] = ['suspend', 'unsuspend', 'ban', 'unban', 'delete'];

// what a signed-in player is told when their account is held
export const AccountHold = z.object({
  kind: z.enum(['suspended', 'banned']),
  until: z.string().nullable(),
  reason: z.string().nullable(),
});
export type AccountHold = z.infer<typeof AccountHold>;
// warnings the player has not seen yet (shown once, then marked seen)
export const PlayerWarning = z.object({ id: z.string().uuid(), text: z.string(), createdAt: z.string() });
export type PlayerWarning = z.infer<typeof PlayerWarning>;
